import React, { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft2,
  ArrowRight2,
  Edit,
  Refresh,
  SearchNormal,
  Sort,
} from "iconsax-react";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";
import CreateOrder from "../../../components/modals/order/CreateOrder";
import { PaginationButton } from "../../../components/PaginationButton";
import {
  NewOrder,
  RefillOrder,
} from "../../../components/order_types/order_types";
import EditOrder from "../../../components/modals/order/EditOrder";
import {
  MedicationName,
  RxMedicationName,
} from "../../../components/MedicationName";

export const refillRequestsLoader = async ({ params }) => {
  const token = getToken();
  const rrs = await instance.get("/doctor/refill/pending", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    rrs: rrs.data,
  };
};

const RefillRequestsPage = () => {
  let { rrs } = useLoaderData();
  const location = useLocation();
  const [refillRequests, setRefillRequests] = useState(
    [...rrs].map((_, index) => ({ ..._, index: index + 1 }))
  );
  const [filteredRefillRequests, setFilteredRefillRequests] = useState(
    refillRequests.filter((_, index) => index + 1 <= 10)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setRefillRequests(() => rrs.map((_, index) => ({ ..._, index: index + 1 })));
  }, [rrs]);

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredRefillRequests(() => {
      return refillRequests.filter((rr) => {
        const id = `${rr.id}` === query;
        const patientLastName = rr.patient.lastname
          .toLowerCase()
          .includes(query);
        const patientFirstName = rr.patient.firstname
          .toLowerCase()
          .includes(query);
        const pharmacyName = rr.pharmacy.pharmacy_name
          .toLowerCase()
          .includes(query);
        const medicationName = rr.medication_name.toLowerCase().includes(query);

        return (
          id ||
          patientFirstName ||
          patientLastName ||
          medicationName ||
          pharmacyName
        );
      });
    });
  };

  useEffect(() => {
    setFilteredRefillRequests(() =>
      refillRequests.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex]);

  const tabs = [
    {
      id: 1,
      title: "Pending",
      href: `/dashboard/refill-requests`,
    },
    {
      id: 2,
      title: "Approved",
      href: `/dashboard/refill-requests/approved`,
    },
    {
      id: 3,
      title: "Declined",
      href: `/dashboard/refill-requests/declined`,
    },
  ];

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        refillRequests.reverse();
        setRefillRequests(refillRequests);
        setFilteredRefillRequests((items) =>
          refillRequests.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "medication-name": {
        setPageIndex(1);
        if (sorted === "medication-name") {
          setSorted("");
          refillRequests.sort((a, b) => a.index - b.index);
        } else {
          setSorted("medication-name");
          refillRequests.sort((a, b) => {
            if (a.medicationRx.medication_name < b.medicationRx.medication_name)
              return -1;
            if (a.medicationRx.medication_name > b.medicationRx.medication_name)
              return 1;
            return 0;
          });
        }
        setFilteredRefillRequests((items) =>
          refillRequests.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "pharmacy": {
        setPageIndex(1);
        if (sorted === "pharmacy") {
          setSorted("");
          refillRequests.sort((a, b) => a.index - b.index);
        } else {
          setSorted("pharmacy");
          refillRequests.sort((a, b) => {
            if (a.pharmacy.pharmacy_name < b.pharmacy.pharmacy_name) return -1;
            if (a.pharmacy.pharmacy_name > b.pharmacy.pharmacy_name) return 1;
            return 0;
          });
        }
        setFilteredRefillRequests((items) =>
          refillRequests.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "patient": {
        setPageIndex(1);
        if (sorted === "patient") {
          setSorted("");
          refillRequests.sort((a, b) => a.index - b.index);
        } else {
          setSorted("patient");
          refillRequests.sort((a, b) => {
            if (a.patient.lastname < b.patient.lastname) return -1;
            if (a.patient.lastname > b.patient.lastname) return 1;
            return 0;
          });
        }
        setFilteredRefillRequests((items) =>
          refillRequests.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          refillRequests.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          refillRequests.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        }
        setFilteredRefillRequests((items) =>
          refillRequests.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
    }
  };

  return (
    <div className="px-7 py-5 bg-[#fafafa]">
      <div className="flex justify-between items-center mb-5">
        <div className="font-bold text-2xl">
          Refill Requests ({refillRequests.length})
        </div>
      </div>

      <div className="bg-[#fafafa] px-7 -mx-7 flex border-t border-b overflow-x-scroll scrollbar-hide whitespace-nowrap">
        {tabs.map((item) => (
          <Link
            to={item.href}
            key={item.id}
            className={`${
              location.pathname === item.href
                ? "bg-[#00b0ad0d] border-[#00B0AD] text-black font-medium"
                : "font-medium text-gray-800 border-b-transparent"
            } border-t-[3px] border-t-transparent border-b-[3px]  cursor-pointer p-3 text-[13px] select-none`}
          >
            {item.title}
          </Link>
        ))}
      </div>

      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search refill request by RX number"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(refillRequests.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">RX Number</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("medication-name")}
                >
                  Medication Name{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "medication-name" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("pharmacy")}
                >
                  Pharmacy{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "pharmacy" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("patient")}
                >
                  Patient{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "patient" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td></td>
              </tr>
            </thead>

            <tbody>
              {refillRequests.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No pending refill requests
                  </td>
                </tr>
              ) : (
                filteredRefillRequests.map((rr, index) => (
                  <tr key={rr.index} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-4">{rr.index}</td>
                    <td className="px-6 py-4">{rr.rx_number}</td>
                    <td className="px-6 py-4">
                      <RxMedicationName rx={rr.medicationRx} />
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {rr.pharmacy.pharmacy_name}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {rr.patient.lastname} {rr.patient.firstname}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(rr.created_at).toLocaleString().split(",")[0]}
                    </td>
                    <td
                      onClick={() =>
                        navigate(`/dashboard/refill-requests/${rr.id}`)
                      }
                      className="px-6 py-4 underline text-primary cursor-pointer"
                    >
                      View Refill Request
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Outlet />
    </div>
  );
};

export default RefillRequestsPage;
